import {
  fetchCatalog,
  buildTree,
  photosForFolder,
  firstSelectableFolder,
  labeledBreadcrumb,
  type FolderNode,
  type ResolvedPhoto,
} from './catalog';
import type { CatalogFile } from '../../shared/catalog-types';
import { decodePath, pushRoute, replaceRoute } from './router';

type HistoryMode = 'replace' | 'push' | 'none';

/**
 * Central application state. A single shared instance drives every component,
 * keeping the current folder/photo, the URL, and the listing-panel highlight in
 * lockstep. Reactivity is provided by Svelte 5 runes.
 */
class Gallery {
  status = $state<'loading' | 'ready' | 'error'>('loading');
  errorMessage = $state<string | null>(null);
  catalog = $state<CatalogFile | null>(null);
  tree = $state<FolderNode | null>(null);

  folderPath = $state('');
  photoId = $state<string | null>(null);
  /**
   * The folder currently being browsed in the menu listing. Separate from
   * folderPath so that clicking a category only changes what the listing shows —
   * the photo on screen stays put until the user actually picks one.
   */
  listPath = $state('');
  panelOpen = $state(false);
  /** First-visit hint to scroll/swipe for more; dismissed on first interaction. */
  showHint = $state(false);

  /** Photos in the current folder, in catalog order. */
  photos = $derived<ResolvedPhoto[]>(
    this.catalog ? photosForFolder(this.catalog, this.folderPath) : [],
  );
  index = $derived(this.photos.findIndex((p) => p.id === this.photoId));
  current = $derived<ResolvedPhoto | null>(
    this.index >= 0 ? this.photos[this.index] : null,
  );
  /** Readable labels for the current folder path, e.g. ["Travel", "New Zealand"]. */
  breadcrumb = $derived<string[]>(
    this.tree ? labeledBreadcrumb(this.tree, this.folderPath) : [],
  );

  /** Photos shown in the menu listing (the folder being browsed, not viewed). */
  listPhotos = $derived<ResolvedPhoto[]>(
    this.catalog ? photosForFolder(this.catalog, this.listPath) : [],
  );
  /** Readable labels for the folder being browsed in the menu. */
  listBreadcrumb = $derived<string[]>(
    this.tree ? labeledBreadcrumb(this.tree, this.listPath) : [],
  );
  /**
   * The on-screen photo's id, but only when the listing is showing that photo's
   * own folder — so the highlight appears only on the folder it belongs to.
   */
  listActiveId = $derived<string | null>(
    this.listPath === this.folderPath ? this.photoId : null,
  );

  async init(): Promise<void> {
    try {
      const catalog = await fetchCatalog();
      this.catalog = catalog;
      this.tree = buildTree(catalog);
      this.applySegments(decodePath(location.pathname), 'replace');
      this.status = 'ready';
      // Show the scroll/swipe hint on every load; dismissed on first scroll/swipe.
      this.showHint = true;
    } catch (e) {
      this.errorMessage = e instanceof Error ? e.message : String(e);
      this.status = 'error';
    }

    window.addEventListener('popstate', () => {
      this.applySegments(decodePath(location.pathname), 'none');
    });
  }

  /**
   * Resolve URL path segments into a concrete folder + photo selection.
   * A fully-matching folder path wins; otherwise the last segment is treated as
   * a photo id within the folder formed by the preceding segments. Falls back to
   * the first folder / first photo when nothing matches.
   */
  applySegments(segments: string[], history: HistoryMode): void {
    if (!this.catalog || !this.tree) return;

    let folderPath = '';
    let photoId: string | null = null;

    const joined = segments.join('/');
    if (joined && this.isFolder(joined)) {
      folderPath = joined;
    } else if (segments.length) {
      const maybePhoto = segments[segments.length - 1];
      const maybeFolder = segments.slice(0, -1).join('/');
      if (
        this.isFolder(maybeFolder) &&
        photosForFolder(this.catalog, maybeFolder).some((p) => p.id === maybePhoto)
      ) {
        folderPath = maybeFolder;
        photoId = maybePhoto;
      }
    }

    if (!folderPath) folderPath = firstSelectableFolder(this.tree) ?? '';

    const photos = folderPath ? photosForFolder(this.catalog, folderPath) : [];
    if (!photoId || !photos.some((p) => p.id === photoId)) {
      photoId = photos[0]?.id ?? null;
    }

    this.folderPath = folderPath;
    this.photoId = photoId;
    this.listPath = folderPath;
    this.syncHistory(history);
  }

  /** Browse a folder in the menu listing. The on-screen photo is left unchanged
   * — the user picks a photo from the listing to actually navigate to it. */
  selectFolder(path: string): void {
    if (!this.catalog || path === this.listPath) return;
    this.listPath = path;
  }

  /** Open a photo from the menu listing: commit the browsed folder and close. */
  openPhoto(id: string): void {
    this.folderPath = this.listPath;
    this.photoId = id;
    pushRoute(this.folderPath, this.photoId);
    this.closePanel();
  }

  selectPhoto(id: string): void {
    if (id === this.photoId) return;
    this.photoId = id;
    pushRoute(this.folderPath, this.photoId);
  }

  step(delta: number): void {
    if (this.index < 0) return;
    const target = this.index + delta;
    if (target < 0 || target >= this.photos.length) return;
    this.selectPhoto(this.photos[target].id);
  }

  /** Advance to the next photo, or open the menu if already on the last one. */
  forward(): void {
    if (this.index >= 0 && this.index >= this.photos.length - 1) this.openPanel();
    else this.step(1);
  }

  /** Hide the scroll/swipe hint (on first interaction). Shows again next load. */
  dismissHint(): void {
    this.showHint = false;
  }

  openPanel(): void {
    // Start browsing from the current photo's folder each time the menu opens.
    this.listPath = this.folderPath;
    this.panelOpen = true;
  }
  closePanel(): void {
    this.panelOpen = false;
  }
  togglePanel(): void {
    if (this.panelOpen) this.closePanel();
    else this.openPanel();
  }

  private isFolder(path: string): boolean {
    return !!this.catalog?.folders.some((f) => f.path === path);
  }

  private syncHistory(mode: HistoryMode): void {
    if (mode === 'replace') replaceRoute(this.folderPath, this.photoId);
    else if (mode === 'push') pushRoute(this.folderPath, this.photoId);
  }
}

export const gallery = new Gallery();
